import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Controller, type ControllerRenderProps, type FieldValues } from 'react-hook-form';
import { $getRoot, $isParagraphNode, $isTextNode, KEY_DOWN_COMMAND } from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';

import './DynamicTextEditorStyle.css';
import '../../latex/renderer/latexStyle.css';
import ImagePastePlugin from './plugins/ImagePastePlugin';
import ImageUrlRevokePlugin from './plugins/ImageUrlRevokePlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import LatexNode from './nodes/LatexNode';
import SimpleLatexNode from './nodes/SimpleLatexNode';
import { ImageNode } from './nodes/ImageNode';
import { getNodeClasses } from './utils';
import { importHtmlToLexical, isEditorContentEmpty } from '../utils';
import { ErrorMessage } from '@/src/ui';

const theme = {
    text: {
        bold: 'dynamic-bold',
        italic: 'dynamic-italic',
        underline: 'dynamic-underline',
    },
};

const initialConfig = {
    namespace: 'LatexRenderer',
    theme,
    onError(error: Error) {
        // console.error("Lexical error:", error);
        return error;
    },
    nodes: [LatexNode, SimpleLatexNode, ImageNode],
};

function saveNode(htmlParts: string[], node: any) {
    if ($isParagraphNode(node)) {
        const childHtml: string[] = [];

        node.getChildren().forEach((child) => {
            if ($isTextNode(child)) {
                const text = child.getTextContent();
                const style = child.getStyle().trim();

                const classes = getNodeClasses(child);
                const classAttr = classes.length > 0 ? ` class="${classes.join(' ')}"` : '';
                childHtml.push(
                    `<span${classAttr}${style ? ` style="${style}"` : ''}>${text}</span>`
                );
            } else if (child.__type === 'latex') {
                const latex = (child as any).__latex ?? '';
                const color = (child as any).__color ?? 'inherit';
                childHtml.push(`<latex${color ? ` style="color: ${color}"` : ''}>${latex}</latex>`);
            } else if (child.__type === 'simpleLatex') {
                const latex = (child as any).__latex ?? '';
                childHtml.push(`<latex>${latex}</latex>`);
            } else if (child.__type === 'image') {
                const src = (child as any).__src ?? '';
                const alt = (child as any).__alt || 'img';
                const width = (child as any).__width;
                const height = (child as any).__height;
                childHtml.push(
                    `<img src="${src}" alt="${alt}"${width ? ` width="${width}"` : ''}${height ? ` height="${height}"` : ''} />`
                );
            } else {
                childHtml.push('[Unknown Node]');
            }
        });

        // Handle alignment styles
        const alignment = node.getFormatType?.() || '';
        const alignmentStyle = alignment ? ` style="text-align: ${alignment};"` : '';

        htmlParts.push(`<p${alignmentStyle}>${childHtml.join('')}</p>`);
    }
}

export type CDTEFieldType = ControllerRenderProps<FieldValues, string>;

interface ControlledDynamicTextEditorProps {
    initialValue: string;
    setOutput?: (val: string, field: ControllerRenderProps<FieldValues, string>) => void;
    name: string;
    label?: string;
    control: any;
    simpleMode?: boolean;
    disableImages?: boolean;
    error?: any;
    style?: React.CSSProperties;
    disabled?: boolean;
}

export function ControlledDynamicTextEditor({
    initialValue,
    setOutput,
    name,
    label,
    control,
    simpleMode,
    disableImages,
    error,
    style = {},
    disabled = false,
}: ControlledDynamicTextEditorProps) {
    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <DynamicTextEditor
                    label={label}
                    initialValue={initialValue}
                    setOutput={(val) => {
                        return setOutput ? setOutput(val, field) : field.onChange(val);
                    }}
                    simpleMode={simpleMode}
                    disableImages={disableImages}
                    error={error}
                    style={style}
                    disabled={disabled}
                />
            )}
        />
    );
}

interface DynamicTextEditorProps {
    label?: string;
    initialValue?: string;
    setOutput: (output: string) => void;
    simpleMode?: boolean;
    disableImages?: boolean;
    error?: any;
    style?: React.CSSProperties;
    disabled?: boolean;
}

export function DynamicTextEditor(props: DynamicTextEditorProps) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [editorFocused, setEditorFocused] = useState(false);

    const outerStyle = useMemo(() => {
        const style = props.style || {};

        const newStyle: React.CSSProperties = {};
        if ('minWidth' in style) newStyle.minWidth = style.minWidth;
        if ('width' in style) newStyle.width = style.width;
        if ('maxWidth' in style) newStyle.maxWidth = style.maxWidth;
        return newStyle;
    }, [props.style]);

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <DynamicTextEditorInner
                wrapperRef={wrapperRef as React.RefObject<HTMLDivElement>}
                editorFocused={editorFocused}
                setEditorFocused={setEditorFocused}
                outerStyle={outerStyle}
                dteProps={props}
            />
        </LexicalComposer>
    );
}

interface DynamicTextEditorInnerProps {
    wrapperRef: React.RefObject<HTMLDivElement>;
    editorFocused: boolean;
    setEditorFocused: React.Dispatch<React.SetStateAction<boolean>>;
    outerStyle: React.CSSProperties;
    dteProps: DynamicTextEditorProps;
}

// Split out the inner part so useLexicalComposerContext works
function DynamicTextEditorInner({
    wrapperRef,
    editorFocused,
    setEditorFocused,
    outerStyle,
    dteProps,
}: DynamicTextEditorInnerProps) {
    const [loaded, setLoaded] = useState(false);
    const [editor] = useLexicalComposerContext(); // Need to split outer/inner because of this line
    const lastHtmlRef = useRef<string>('');
    const editorLoadingRef = useRef<boolean>(false); // Prevent setOutput call until text has changed --
    // -- focusing on the editor to will trigger an update, setting this to false before the user enters text

    const { simpleMode, disableImages, style, label, initialValue, error, setOutput, disabled } =
        dteProps;

    useEffect(() => {
        if (!editor || initialValue == null || loaded) return;
        editorLoadingRef.current = true;
        editor.getEditorState().read(() => {
            const root = $getRoot();
            const htmlParts: string[] = [];
            root.getChildren().forEach((node) => saveNode(htmlParts, node));
            const currentHtml = htmlParts.join('\n');
            if (isEditorContentEmpty(currentHtml) && !isEditorContentEmpty(initialValue)) {
                setLoaded(true);
                editor.update(() => {
                    const root = $getRoot();
                    root.clear();
                    const nodes = importHtmlToLexical(initialValue);
                    nodes.forEach((node) => root.append(node));
                });
            }
        });
        editorLoadingRef.current = false;
    }, [editor, initialValue, loaded]);

    // Block Enter in simpleMode
    useEffect(() => {
        if (!editor) {
            return;
        }

        const unregisterKeyDown = editor.registerCommand(
            KEY_DOWN_COMMAND,
            (event: KeyboardEvent) => {
                if (simpleMode && event.key === 'Enter') {
                    event.preventDefault();
                    return true; // handled
                }
                return false; // let others handle
            },
            0
        );

        return () => {
            unregisterKeyDown();
        };
    }, [editor, simpleMode]);

    // Custom OnChange handler to avoid render loop
    const handleChange = useCallback(
        (editorState: any) => {
            editorState.read(() => {
                const root = $getRoot();
                const htmlParts: string[] = [];
                root.getChildren().forEach((node) => saveNode(htmlParts, node));
                const finalHtml = htmlParts.join('\n');
                if (lastHtmlRef.current !== finalHtml) {
                    lastHtmlRef.current = finalHtml;
                    if (!editorLoadingRef.current) {
                        setOutput(finalHtml);
                    }

                    editorLoadingRef.current = false;
                }
            });
        },
        [setOutput]
    );

    return (
        <div>
            <div
                ref={wrapperRef as React.RefObject<HTMLDivElement>}
                className="dynamic-container"
                style={outerStyle}
            >
                {label && <label style={{ fontSize: '10pt' }}>{label}</label>}
                {editorFocused && (
                    <ToolbarPlugin simpleMode={simpleMode} setEditorFocused={setEditorFocused} />
                )}
                <div className="dynamic-inner">
                    <RichTextPlugin
                        contentEditable={
                            <ContentEditable
                                className="dynamic-input"
                                style={{
                                    ...style,
                                    backgroundColor: disabled ? '#eaeaeaff' : undefined,
                                    color: disabled ? '#6a6a6aff' : undefined,
                                    cursor: disabled ? 'not-allowed' : undefined,
                                }}
                                onFocus={() => {
                                    if (!disabled) setEditorFocused(true);
                                }}
                                contentEditable={!disabled}
                            />
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <HistoryPlugin />
                    {!disableImages && <ImagePastePlugin />}
                    {!disableImages && <ImageUrlRevokePlugin />}
                    <OnChangePlugin onChange={handleChange} />
                </div>
            </div>
            <ErrorMessage error={error} />
        </div>
    );
}
